import _ from "lodash";

export const LABELS_STYLESHEET_FRAGMENT_KEYS = ["item", "labels", "label", "label1", "label2"];
export const MARKDOWN_STYLESHEET_FRAGMENT_KEY = "markdown";

const pruneEmptyStyleTree = (value) => {
	if (_.isArray(value)) {
		const nextArray = value
			.map(pruneEmptyStyleTree)
			.filter((entry) => !(_.isUndefined(entry) || entry === null || entry === ""));
		return nextArray.length > 0 ? nextArray : undefined;
	}
	if (_.isPlainObject(value)) {
		const nextObject = {};
		_.forOwn(value, (childValue, childKey) => {
			const nextChildValue = pruneEmptyStyleTree(childValue);
			if (!(_.isUndefined(nextChildValue) || nextChildValue === null || nextChildValue === "")) {
				nextObject[childKey] = nextChildValue;
			}
		});
		return _.isEmpty(nextObject) ? undefined : nextObject;
	}
	return value;
};

export const parseJsonStyleObject = (rawValue = "") => {
	const nextRawValue = rawValue === null ? "" : rawValue;
	if (!nextRawValue || (_.isString(nextRawValue) && nextRawValue.trim() === "")) {
		return {};
	}
	if (_.isPlainObject(nextRawValue)) {
		return _.cloneDeep(nextRawValue);
	}
	try {
		const parsed = JSON.parse(nextRawValue);
		return _.isPlainObject(parsed) ? parsed : {};
	} catch (e) {
		return null;
	}
};

export const stringifyJsonStyleObject = (nextStyles = {}) => {
	const cleanedStyles = pruneEmptyStyleTree(nextStyles) || {};
	return _.isEmpty(cleanedStyles) ? "{}" : JSON.stringify(cleanedStyles, null, 2);
};

export const getStylesheetScopeSelector = (scopeType = "activity", scopeEntity = null) => {
	if (!scopeType || scopeType === "activity") {
		return null;
	}
	const scopeId = _.trim(_.get(scopeEntity, "guid") || _.get(scopeEntity, "id") || "");
	return scopeId === "" ? null : `@${scopeType}:${scopeId}`;
};

export const getLabelsStylesheetScopeSelector = getStylesheetScopeSelector;

const getScopedStylesheetKey = (scopeSelector = null, styleKey = "") => {
	return scopeSelector ? `${scopeSelector} ${styleKey}` : styleKey;
};

export const extractLabelsStyleFragmentFromStylesheet = (stylesheetValue = "", scopeSelector = null) => {
	const stylesheetObject = parseJsonStyleObject(stylesheetValue);
	if (stylesheetObject === null) {
		return null;
	}
	const fragment = {};
	LABELS_STYLESHEET_FRAGMENT_KEYS.forEach((styleKey) => {
		const scopedKey = getScopedStylesheetKey(scopeSelector, styleKey);
		if (Object.prototype.hasOwnProperty.call(stylesheetObject, scopedKey)) {
			fragment[styleKey] = _.cloneDeep(stylesheetObject[scopedKey]);
		}
	});
	return fragment;
};

export const mergeLabelsStyleFragmentIntoStylesheet = (stylesheetValue = "", fragmentValue = "", scopeSelector = null) => {
	const stylesheetObject = parseJsonStyleObject(stylesheetValue);
	const fragmentObject = parseJsonStyleObject(fragmentValue);
	if (stylesheetObject === null || fragmentObject === null) {
		return null;
	}

	LABELS_STYLESHEET_FRAGMENT_KEYS.forEach((styleKey) => {
		delete stylesheetObject[getScopedStylesheetKey(scopeSelector, styleKey)];
	});

	const cleanedFragment = pruneEmptyStyleTree(fragmentObject) || {};
	LABELS_STYLESHEET_FRAGMENT_KEYS.forEach((styleKey) => {
		if (Object.prototype.hasOwnProperty.call(cleanedFragment, styleKey)) {
			stylesheetObject[getScopedStylesheetKey(scopeSelector, styleKey)] = cleanedFragment[styleKey];
		}
	});

	return stringifyJsonStyleObject(stylesheetObject);
};

export const extractMarkdownStyleFragmentFromStylesheet = (stylesheetValue = "", scopeSelector = null) => {
	const stylesheetObject = parseJsonStyleObject(stylesheetValue);
	if (stylesheetObject === null) {
		return null;
	}
	const scopedKey = getScopedStylesheetKey(scopeSelector, MARKDOWN_STYLESHEET_FRAGMENT_KEY);
	const fragment = Object.prototype.hasOwnProperty.call(stylesheetObject, scopedKey)
		? stylesheetObject[scopedKey]
		: {};
	return _.isPlainObject(fragment) ? _.cloneDeep(fragment) : {};
};

export const mergeMarkdownStyleFragmentIntoStylesheet = (stylesheetValue = "", fragmentValue = "", scopeSelector = null) => {
	const stylesheetObject = parseJsonStyleObject(stylesheetValue);
	const fragmentObject = parseJsonStyleObject(fragmentValue);
	if (stylesheetObject === null || fragmentObject === null) {
		return null;
	}

	const scopedKey = getScopedStylesheetKey(scopeSelector, MARKDOWN_STYLESHEET_FRAGMENT_KEY);
	delete stylesheetObject[scopedKey];

	const cleanedFragment = pruneEmptyStyleTree(fragmentObject) || {};
	if (!_.isEmpty(cleanedFragment)) {
		stylesheetObject[scopedKey] = cleanedFragment;
	}

	return stringifyJsonStyleObject(stylesheetObject);
};
